import { useState } from "react";

import directorApi from "@contexts/director/api";
import { compactCents, formatCivilDate, formatFreshness } from "../../../utils/directorFormat";

// Títulos retirados da análise da Diretoria (SPEC 2026-10-19): a lista fica sempre
// visível, com motivo, autor e o que cada título deixou de somar no último ciclo.

export const CATEGORY_LABELS = {
  erp_forecast: "Previsão do ERP (PRV)",
  duplicate: "Duplicado",
  out_of_scope: "Fora do escopo da análise",
  other: "Outro motivo",
};

export const AUTOMATIC_LABELS = {
  erp_forecast_with_schedule: "Previsão do ERP em obra com cronograma: o cronograma vence",
  erp_forecast_receivable: "Previsão de receita do ERP",
};

const SIDE_LABELS = { outflow: "A pagar", inflow: "A receber" };

const civil = (value) => (typeof value === "string" ? formatCivilDate(value.slice(0, 10)) : "—");

const dueRange = (effect) => {
  if (!effect?.firstDueDate) return "—";
  return effect.firstDueDate === effect.lastDueDate
    ? civil(effect.firstDueDate)
    : `${civil(effect.firstDueDate)} a ${civil(effect.lastDueDate)}`;
};

const effectText = (item, lastCycleAt) => {
  if (item.state !== "active") return "Volta a entrar a partir do próximo ciclo";
  if (item.effect) {
    return `${compactCents(item.effect.excludedCents)} · ${item.effect.installmentCount} parcela(s) · ${dueRange(item.effect)}`;
  }
  if (!lastCycleAt || (item.createdAt && item.createdAt > lastCycleAt)) return "Sai da análise no próximo ciclo";
  return "Não encontrado no último ciclo";
};

const person = (value) => value?.name || "Usuário";

const errorMessage = (result) => (
  result?.body?.message || (result?.status === 403
    ? "Você não tem permissão para alterar a lista."
    : "Não foi possível salvar. Tente de novo.")
);

const emptyForm = (legalEntities) => ({
  legalEntityId: legalEntities.length === 1 ? String(legalEntities[0].id) : "",
  billId: "",
  direction: "outflow",
  category: "erp_forecast",
  reason: "",
});

const TitleExclusionsPanel = ({ surface, companyId, api = directorApi }) => {
  const { data } = surface;
  const legalEntities = data?.legalEntities || [];
  const [form, setForm] = useState(() => emptyForm(legalEntities));
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);
  if (!data) return null;
  const items = data.items || [];
  const automatic = data.automatic || [];
  const active = items.filter((item) => item.state === "active");
  const history = items.filter((item) => item.state !== "active");
  const field = (name) => (event) => setForm((current) => ({ ...current, [name]: event.target.value }));

  const run = async (action, success) => {
    setBusy(true);
    setMessage(null);
    const result = await action();
    setBusy(false);
    if (result?.ok) {
      setMessage({ tone: "success", text: success });
      surface.reload();
      return true;
    }
    setMessage({ tone: "error", text: errorMessage(result) });
    return false;
  };

  const withdraw = async (event) => {
    event.preventDefault();
    const billId = Number(form.billId);
    const ok = await run(() => api.withdrawDirectorTitle(companyId, {
      legalEntityId: Number(form.legalEntityId),
      billId,
      direction: form.direction,
      category: form.category,
      reason: form.reason,
    }), `Título ${billId} retirado. Vale a partir do próximo ciclo de atualização.`);
    if (ok) setForm(emptyForm(legalEntities));
  };

  const reinclude = (item) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Reincluir o título ${item.billId} na análise?`)) return;
    run(
      () => api.reincludeDirectorTitle(companyId, item.id),
      `Título ${item.billId} reincluído. Volta a entrar a partir do próximo ciclo.`,
    );
  };

  return <section className="director-title-exclusions" aria-labelledby="director-title-exclusions-title">
    <header className="director-module1__section-header">
      <span className="director-eyebrow">Títulos retirados da análise</span>
      <h2 id="director-title-exclusions-title">O que não entra nos números da Diretoria</h2>
      <p>
        Títulos do Sienge que ficam fora de caixa, compromissos e capital. Cada retirada guarda o motivo,
        quem retirou e quanto deixou de somar.
        {data.lastCycleAt ? ` Último ciclo em ${formatFreshness(data.lastCycleAt)}.` : " Ainda sem ciclo publicado com a lista."}
      </p>
    </header>
    {message && <p className="director-title-exclusions__message" data-tone={message.tone} role="status">{message.text}</p>}
    <div className="director-table-scroll">
      <table className="director-table director-title-exclusions__table" aria-label="Títulos retirados manualmente">
        <thead>
          <tr>
            <th scope="col">Título</th>
            <th scope="col">Empresa no Sienge</th>
            <th scope="col">Lado</th>
            <th scope="col">Motivo</th>
            <th scope="col">Retirado por</th>
            <th scope="col">Efeito no último ciclo</th>
            {data.canWrite && <th scope="col"><span className="visually-hidden">Ação</span></th>}
          </tr>
        </thead>
        <tbody>
          {active.length === 0 && <tr><td colSpan={data.canWrite ? 7 : 6}>Nenhum título retirado manualmente.</td></tr>}
          {active.map((item) => <tr key={item.id}>
            <th scope="row">{item.billId}</th>
            <td>{item.legalEntityName || item.legalEntityId}</td>
            <td>{SIDE_LABELS[item.direction] || item.direction}</td>
            <td><strong>{CATEGORY_LABELS[item.category] || item.category}</strong><br />{item.reason}</td>
            <td>{person(item.createdBy)}, {civil(item.createdAt)}</td>
            <td>{effectText(item, data.lastCycleAt)}</td>
            {data.canWrite && <td>
              <button className="director-button director-button--secondary" type="button" disabled={busy} onClick={() => reinclude(item)}>
                Reincluir
              </button>
            </td>}
          </tr>)}
        </tbody>
      </table>
    </div>
    {automatic.length > 0 && <div className="director-table-scroll">
      <table className="director-table director-title-exclusions__table" aria-label="Títulos retirados automaticamente">
        <thead>
          <tr>
            <th scope="col">Título</th>
            <th scope="col">Empresa no Sienge</th>
            <th scope="col">Lado</th>
            <th scope="col">Regra</th>
            <th scope="col">Valor fora da análise</th>
            <th scope="col">Vencimentos</th>
          </tr>
        </thead>
        <tbody>
          {automatic.map((item) => <tr key={`${item.origin}-${item.legalEntityId}-${item.billId}-${item.direction}`}>
            <th scope="row">{item.billId}</th>
            <td>{item.legalEntityName || item.legalEntityId}</td>
            <td>{SIDE_LABELS[item.direction] || item.direction}</td>
            <td>{AUTOMATIC_LABELS[item.origin] || item.origin}</td>
            <td>{compactCents(item.excludedCents)} · {item.installmentCount} parcela(s)</td>
            <td>{dueRange(item)}</td>
          </tr>)}
        </tbody>
      </table>
    </div>}
    {history.length > 0 && <details className="director-title-exclusions__history">
      <summary>Reincluídos ({history.length})</summary>
      <ul>
        {history.map((item) => <li key={item.id}>
          Título {item.billId} ({SIDE_LABELS[item.direction] || item.direction}, {item.legalEntityName || item.legalEntityId}):
          retirado por {person(item.createdBy)} em {civil(item.createdAt)}, reincluído por {person(item.revokedBy)} em {civil(item.revokedAt)}.
          Motivo da retirada: {item.reason}
        </li>)}
      </ul>
    </details>}
    {data.canWrite && <form className="director-title-exclusions__form" onSubmit={withdraw} aria-label="Retirar um título da análise">
      <h3>Retirar um título</h3>
      <label className="director-filter-field">
        <span>Empresa no Sienge</span>
        <select required value={form.legalEntityId} onChange={field("legalEntityId")}>
          <option value="" disabled>Escolha</option>
          {legalEntities.map((entity) => <option key={entity.id} value={entity.id}>{entity.name}</option>)}
        </select>
      </label>
      <label className="director-filter-field">
        <span>Número do título</span>
        <input required inputMode="numeric" pattern="[0-9]{1,9}" value={form.billId} onChange={field("billId")} />
      </label>
      <label className="director-filter-field">
        <span>Lado</span>
        <select value={form.direction} onChange={field("direction")}>
          <option value="outflow">A pagar</option>
          <option value="inflow">A receber</option>
        </select>
      </label>
      <label className="director-filter-field">
        <span>Categoria</span>
        <select value={form.category} onChange={field("category")}>
          {Object.entries(CATEGORY_LABELS).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </select>
      </label>
      <label className="director-filter-field director-title-exclusions__reason">
        <span>Motivo</span>
        <textarea required minLength={3} maxLength={500} value={form.reason} onChange={field("reason")} />
      </label>
      <button className="director-button" type="submit" disabled={busy}>Retirar da análise</button>
      <small>Vale a partir do próximo ciclo de atualização. Dá para reincluir a qualquer momento.</small>
    </form>}
  </section>;
};

export default TitleExclusionsPanel;
